import React, { useState, useRef, useEffect } from 'react';
import { X, Check, ChevronDown } from 'lucide-react';

interface Option {
  value: string | undefined;
  label: string | React.ReactNode;
  description?: string;
  searchText: string; // Required field for searching
}

interface MultiSelectFieldProps {
  id: string;
  value: string[];
  onChange: (values: string[]) => void;
  options: Option[];
  disabled?: boolean;
  placeholder?: string;
}

const MultiSelectField: React.FC<MultiSelectFieldProps> = ({
  id,
  value,
  onChange,
  options,
  disabled,
  placeholder = 'Select items...'
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);

  const selectedOptions = options.filter(opt => value.includes(opt.value ?? ''));
  
  const filteredOptions = options.filter(opt => 
    opt.searchText.toLowerCase().includes(searchTerm.toLowerCase()) &&
    !value.includes(opt.value ?? '')
  );

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (optionValue: string) => {
    onChange([...value, optionValue]);
    setSearchTerm('');
  };

  const handleRemove = (optionValue: string) => {
    onChange(value.filter(v => v !== optionValue));
  };

  const renderOptionContent = (option: Option) => {
    if (typeof option.label === 'string') {
      return (
        <div className="flex flex-col">
          <span className="text-sm font-small">{option.label}</span>
          {option.description && (
            <span className="text-xs text-gray-500 dark:text-gray-400">
              {option.description}
            </span>
          )}
        </div>
      );
    }
    return option.label;
  };

  return (
    <div className="relative" ref={containerRef}>
      <div
        className={`min-h-10 w-full rounded border border-slate-200 bg-white dark:border-slate-600 
        dark:bg-slate-800 px-2 py-1.5 text-sm text-slate-500 dark:text-slate-300 
        transition-all focus-within:border-secondary dark:focus-within:border-secondary
        ${disabled ? 'bg-gray-100 cursor-not-allowed' : 'cursor-pointer'}`}
        onClick={() => !disabled && setIsOpen(true)}
      >
        <div className="flex flex-wrap gap-1.5">
          {selectedOptions.map(option => (
            <span
              key={option.value}
              className="inline-flex items-center gap-1 rounded bg-primary/10 px-2 py-0.5"
            >
              <span className="text-xs">
                {typeof option.label === 'string' ? 
                  option.label : 
                  <div className="flex items-center">{option.label}</div>
                }
              </span>
              {!disabled && (
                <X
                  className="h-3 w-3 cursor-pointer hover:text-red-500"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRemove(option.value ?? '');
                  }}
                />
              )}
            </span>
          ))}
          {selectedOptions.length === 0 && (
            <span className="text-gray-400">{placeholder}</span>
          )}
        </div>
      </div>

      {isOpen && !disabled && (
        <div className="absolute z-50 mt-1 w-full rounded-md border border-slate-200 bg-white dark:border-slate-600 dark:bg-slate-800 shadow-lg">
          <input
            type="text"
            className="w-full border-b border-slate-200 dark:border-slate-600 p-2 text-sm focus:outline-none dark:bg-slate-800"
            placeholder="Search..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onClick={(e) => e.stopPropagation()}
          />
          <div className="max-h-48 overflow-auto">
            {filteredOptions.length === 0 ? (
              <div className="p-2 text-sm text-gray-400">No options found</div>
            ) : (
              filteredOptions.map(option => (
                <div
                  key={option.value}
                  className="flex items-center px-3 py-2 hover:bg-gray-100 dark:hover:bg-slate-700 cursor-pointer"
                  onClick={() => handleSelect(option.value ?? '')}
                >
                  {renderOptionContent(option)}
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default MultiSelectField;